import React from 'react';
import styled from 'styled-components/macro';
import { usePopper } from 'react-popper';
import classNames from 'classnames';
import chroma from 'chroma-js';
import { SPACINGS } from '../../utils';
import { IconWrapper, IconChevron, IconQadraOlympia } from '../../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { Text } from '../../typography';
import Logo from '../../assets/images/Logo';
import { MenuProps, MenuLinkProps } from './Menu';
import LogoQadraOlympia from '../../assets/images/LogoQadraOlympia';

type MenuDesktopStyledProps = {
  textColor: string;
  backgroundColor: string;
  borderBottom: string;
};
const MenuDesktopStyled = styled.div<MenuDesktopStyledProps>`
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 ${SPACINGS[6]};
  background-color: ${(p) => p.backgroundColor};
  color: ${(p) => p.textColor};
  border-bottom: ${(p) => p.borderBottom ?? 'none'};

  a,
  span,
  a:hover,
  span:hover {
    color: ${(p) => p.textColor};
    text-decoration: none;
  }

  .container {
    margin: auto;
    max-width: 1200px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: env(safe-area-inset-top);
  }

  .logo {
    display: flex;
    align-items: center;
  }

  .navigation {
    display: inline-flex;
    list-style: none;
    align-items: baseline;
    padding: 0;
    margin: 0;

    & > *:not(:last-child) {
      margin-right: ${SPACINGS[4]};
    }

    .item > span {
      position: relative;
    }

    .item > span:before {
      content: '';
      position: absolute;
      background-color: transparent;
      height: 2px;
      right: 0;
      left: 0;
      bottom: 0;
      transition: background-color 0.2s;
    }

    .item.active > span:before {
      content: '';
      position: absolute;
      background-color: ${(p) => p.textColor};
      height: 2px;
      right: 0;
      left: 0;
      bottom: 0;
    }

    .item > span:hover:before {
      content: '';
      position: absolute;
      background-color: ${(p) => chroma(p.textColor).alpha(0.5).hex()};
      height: 2px;
      right: 0;
      left: 0;
      bottom: 0;
    }
  }

  .dropdown-container {
    z-index: ${(p) => p.theme.zIndexes.menu};
  }

  .dropdown {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    list-style: none;
    padding: ${SPACINGS[6]};
    background-color: ${(p) => p.backgroundColor};
    z-index: ${(p) => p.theme.zIndexes.menu};

    li:not(:last-child) {
      margin-bottom: ${SPACINGS[4]};
    }
  }
`;

type ItemProps = {
  linkComponent: (props: MenuLinkProps) => JSX.Element;
  name: string;
  textColor: string;
  href?: string;
  active?: boolean;
  children?: { name: string; href: string; active?: boolean }[];
  itemInHover?: string;
};

function Item(props: ItemProps) {
  const {
    name,
    itemInHover,
    href,
    active,
    linkComponent,
    textColor,
    children,
  } = props;

  const [referenceElement, setReferenceElement] = React.useState(null as any);
  const [popperElement, setPopperElement] = React.useState(null as any);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 50],
        },
      },
    ],
  });

  const [isHover, setIsHover] = React.useState(false);
  const timeoutId = React.useRef<number>();
  function open() {
    clearTimeout(timeoutId.current);
    setIsHover(true);
  }
  function close() {
    timeoutId.current = (setTimeout(() => {
      setIsHover(false);
    }, 2000) as unknown) as number;
  }
  function interruptClose() {
    clearTimeout(timeoutId.current);
  }
  React.useEffect(() => {
    if (itemInHover && itemInHover !== name) {
      interruptClose();
      setIsHover(false);
    }

    return () => interruptClose();
  }, [itemInHover, name]);

  const Link = linkComponent;

  const classes = classNames({
    item: true,
    active: active,
  });

  if (children) {
    return (
      <>
        <li
          className={classes}
          ref={setReferenceElement}
          onMouseEnter={open}
          onMouseLeave={close}
          onFocus={open}
          onBlur={close}
          tabIndex={0}
        >
          <Text size="sm" margin="none">
            {name}
          </Text>
          <IconWrapper>
            <IconChevron stroke={textColor} direction="bottom" />
          </IconWrapper>
        </li>
        <AnimatePresence>
          {isHover && (
            <div
              className="dropdown-container"
              onMouseEnter={interruptClose}
              onMouseLeave={close}
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
            >
              <motion.ul
                className={`${classes} dropdown`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: -10 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ type: 'tween' }}
              >
                {children.map((i) => {
                  const classes = classNames({
                    item: true,
                    active: i.active,
                  });

                  return (
                    <li className={classes} key={i.name}>
                      <Text size="sm" margin="none">
                        <Link href={i.href}>{i.name}</Link>
                      </Text>
                    </li>
                  );
                })}
              </motion.ul>
            </div>
          )}
        </AnimatePresence>
      </>
    );
  }

  if (href) {
    return (
      <li className={classes}>
        <Text size="sm" margin="none">
          <Link href={href}>{name}</Link>
        </Text>
      </li>
    );
  }

  return null;
}

function MenuDesktop<T>(props: MenuProps<T>) {
  const {
    items,
    linkComponent,
    logoLink,
    borderBottom,
    logoColor,
    textColor,
    backgroundColor,
    actions,
    logo,
  } = props;
  const [itemInHover, setHoverItem] = React.useState<string>();

  const logoComponent =
    logo === 'qadra-olympia' ? (
      <LogoQadraOlympia scale={0.3} />
    ) : (
      <Logo scale={0.4} color={logoColor} />
    );

  const Link = linkComponent;
  return (
    <MenuDesktopStyled
      borderBottom={borderBottom!}
      textColor={textColor!}
      backgroundColor={backgroundColor!}
    >
      <div className="container">
        <div className="logo">
          {typeof logoLink === 'string' ? (
            <Link href={logoLink}>{logoComponent}</Link>
          ) : (
            logoLink(logoComponent)
          )}
        </div>
        <ul className="navigation">
          {items.map((i) => (
            <div key={i.name} onMouseEnter={() => setHoverItem(i.name)}>
              <Item
                textColor={textColor!}
                linkComponent={linkComponent}
                itemInHover={itemInHover}
                {...i}
              />
            </div>
          ))}
          {actions}
        </ul>
      </div>
    </MenuDesktopStyled>
  );
}

export default MenuDesktop;
